import { collectValuables } from 'proxyequal';
import functionDouble from 'function-double';
import { shallowEqualHit } from './cache';
import { callIn } from './call';
import { isolatedCall, purityCheck } from './utils';
var defaultOptions = {
  cacheSize: 1,
  shallowCheck: true,
  equalCheck: true,
  strictArity: false,
  nestedEquality: true,
  safe: false
};
export function memoize(func, _options) {
  if (_options === void 0) {
    _options = {};
  }

  var options = Object.assign({}, defaultOptions, _options);
  var cache = [];
  var proxyMap = {};
  var runTimes = 0;
  var executeTimes = 0;
  var cacheHit = 0;
  var cacheMiss = 0;
  var resultSafeChecked = false;
  var memoizationDisabled = false;
  var lastCallWasMemoized = false;

  function functor() {
    runTimes++;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (options.strictArity && func.length) {
      args.length = Math.min(func.length, args.length);
    }

    if (!options.nestedEquality) {
      proxyMap = {};
    }

    if (memoizationDisabled) {
      cacheMiss++;
      return func.call.apply(func, [this].concat(args));
    }

    var found = shallowEqualHit(cache, args);
    lastCallWasMemoized = Boolean(found);

    if (!found) {
      var safeCache;

      if (options.safe && !resultSafeChecked) {
        resultSafeChecked = true;
        safeCache = isolatedCall([], args, func, options.isolatedCheck ? [] : proxyMap);
      }

      cacheMiss++;
      var result = callIn(this, cache, args, func, options.cacheSize, proxyMap, options.flags);
      executeTimes++;

      if (safeCache) {
        memoizationDisabled = !purityCheck(cache, safeCache, func);
      } // test for internal memoization


      return result;
    } else {
      cacheHit++;
      return found.result;
    }
  }

  var memoizedFunction = functionDouble(functor, func, {
    toString: function toString(func) {
      return '/* memoized by memoize-state */\n' + String(func);
    },
    name: function name(func) {
      return 'ms_' + func.name;
    }
  });
  Object.defineProperty(memoizedFunction, 'getAffectedPaths', {
    value: function value() {
      var result = [];

      for (var _i = 0, _cache = cache; _i < _cache.length; _i++) {
        var line = _cache[_i];
        var lineAffected = line.affected;

        for (var argN = 0; argN < lineAffected.length; argN++) {
          if (!result[argN]) {
            result[argN] = {};
          }

          for (var _iterator = collectValuables(lineAffected[argN].useAffected), _isArray = Array.isArray(_iterator), _i2 = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
            var _ref;

            if (_isArray) {
              if (_i2 >= _iterator.length) break;
              _ref = _iterator[_i2++];
            } else {
              _i2 = _iterator.next();
              if (_i2.done) break;
              _ref = _i2.value;
            }

            var key = _ref;
            result[argN][key] = true;
          }
        }
      }

      return result.map(function (values) {
        return Object.keys(values);
      });
    },
    configurable: true,
    enumerable: false
  });
  Object.defineProperty(memoizedFunction, 'cacheStatistics', {
    get: function get() {
      return {
        ratio: cacheHit / cacheMiss,
        memoizationDisabled: memoizationDisabled,
        lastCallArgs: cache.length && cache[cache.length - 1].callArgs,
        cacheHit: cacheHit,
        cacheMiss: cacheMiss,
        runTimes: runTimes,
        executeTimes: executeTimes,
        lastCallWasMemoized: lastCallWasMemoized,
        cache: cache
      };
    },
    configurable: true,
    enumerable: false
  });
  return memoizedFunction;
}